<template>
  <div class="letter-templates-container">
    <!-- Letters Grid -->
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      <div class="mt-4 text-gray-500 font-medium">Fetching letter templates...</div>
    </div>

    <div
      v-else-if="!filteredLetters.length"
      class="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300 mx-4"
    >
      <v-icon size="80" color="grey lighten-2">mdi-file-document-edit-outline</v-icon>
      <h3 class="mt-4 text-xl font-semibold text-gray-700">No Letter Templates</h3>
      <p class="text-gray-500 mt-2 max-w-sm mx-auto">
        Create reusable letter templates that can be attached to your notifications.
      </p>
      <v-btn color="primary" @click="openCreateDialog" outlined class="mt-4 normal-case px-6">
        Create Your First Letter
      </v-btn>
    </div>

    <div v-else class="grid gap-6 md:grid-cols-2 lg:grid-cols-3 px-4">
      <v-card
        v-for="letter in filteredLetters"
        :key="letter._id"
        class="letter-card overflow-hidden border border-gray-200 rounded-md elevation-0"
        hover
      >
        <div class="p-5">
          <div class="d-flex justify-space-between align-start mb-4">
            <div class="bg-blue-50 p-3 rounded-xl">
              <v-icon color="blue darken-2" size="28">mdi-file-pdf-box</v-icon>
            </div>
            <v-menu offset-y left transition="slide-y-transition">
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" color="grey darken-1">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense class="rounded-lg py-1 shadow-lg border border-gray-100">
                <v-list-item @click="editLetter(letter)">
                  <v-list-item-icon class="mr-3"
                    ><v-icon small color="primary">mdi-pencil-outline</v-icon></v-list-item-icon
                  >
                  <v-list-item-title class="font-medium">Edit Template</v-list-item-title>
                </v-list-item>
                <v-divider class="my-1"></v-divider>
                <v-list-item @click="confirmDelete(letter)">
                  <v-list-item-icon class="mr-3"
                    ><v-icon small color="error">mdi-delete-outline</v-icon></v-list-item-icon
                  >
                  <v-list-item-title class="text-red-600 font-medium">Delete</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>

          <h3 class="text-lg font-bold text-gray-800 truncate mb-1">{{ letter.name }}</h3>
          <p class="text-xs text-gray-500 mb-4 font-medium uppercase tracking-wider">
            Last modified: {{ formatDate(letter.updatedAt) }}
          </p>

          <v-divider class="mb-4"></v-divider>

          <div class="d-flex align-center justify-space-between">
            <span class="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded">LETTER</span>
            <div class="d-flex align-center text-xs text-gray-400">
              <v-icon x-small class="mr-1">mdi-calendar-range</v-icon>
              {{ formatDate(letter.createdAt, true) }}
            </div>
          </div>
        </div>
      </v-card>
    </div>

    <!-- Create/Edit Dialog - DESIGN UPDATED TO MATCH AddTemplate.vue -->
    <v-dialog v-model="dialog" persistent width="1200px">
      <v-card flat class="d-flex flex-column p-6" style="height: 90vh">
        <!-- Header -->
        <v-card-title
          class="py-4 px-6 d-flex justify-space-between border-b border-gray-200 top-header flex-shrink-0"
        >
          <h3 class="form-title">
            {{ isEditing ? 'Edit Letter Template' : 'Create New Letter Template' }}
          </h3>
          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Scrollable Content Area -->
        <div class="overflow-y-auto flex-grow-1 p-6 bg-gray-50 p-2">
          <v-row>
            <v-col cols="12">
              <v-card outlined class="rounded-lg pa-4 bg-white border-gray-200">
                <h4 class="text-subtitle-1 font-bold text-gray-800 mb-4">Template Information</h4>
                <v-text-field
                  v-model="form.name"
                  label="Template Name*"
                  placeholder="e.g. welcome_letter, approval_form"
                  outlined
                  dense
                  required
                  class="rounded-lg"
                  hint="Provide a clear name to identify this template when linking it to notifications."
                  persistent-hint
                ></v-text-field>
              </v-card>
            </v-col>

            <v-col cols="12">
              <v-card outlined class="rounded-lg pa-4 bg-white border-gray-200 mt-2">
                <div class="d-flex justify-space-between align-center mb-4">
                  <h4 class="text-subtitle-1 font-bold text-gray-800">Available Parameters</h4>
                  <v-chip size="small" color="info" outlined x-small
                    >Click to insert at cursor</v-chip
                  >
                </div>

                <div class="flex flex-wrap gap-2 mb-2">
                  <v-chip
                    v-for="param in parameterOptions"
                    :key="param.value"
                    @click="insertParameter(param.value)"
                    class="param-chip cursor-pointer bg-blue-50 text-blue-700 font-medium hover:bg-blue-100"
                    size="small"
                    pill
                  >
                    <v-icon left x-small>mdi-plus</v-icon>
                    {{ param.text }}
                  </v-chip>
                </div>
                <p class="text-xs text-gray-500 mt-2">
                  <v-icon x-small color="grey">mdi-information-outline</v-icon>
                  These parameters will be replaced with real data when the letter is generated.
                </p>
              </v-card>
            </v-col>

            <v-col cols="12">
              <v-card outlined class="rounded-lg pa-4 bg-white border-gray-200 mt-2">
                <label class="text-subtitle-1 font-bold text-gray-800 mb-3 d-block"
                  >Letter Designer (PDF Content)</label
                >
                <div class="editor-wrapper border rounded-lg overflow-hidden">
                  <VueEditor
                    v-model="form.content"
                    :editor-toolbar="customToolbar"
                    placeholder="Design your PDF letter here using the rich text tools and parameters above..."
                  />
                </div>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Footer -->
        <div
          class="d-flex justify-end p-4 border-t border-gray-200 flex-shrink-0 bg-white shadow-top"
        >
          <v-btn
            variant="outlined"
            color="primary"
            @click="dialog = false"
            class="mr-3 normal-case rounded-lg px-6"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            @click="saveLetter"
            :loading="saving"
            class="normal-case bg-blue-600 text-white rounded-lg px-8 shadow-sm"
          >
            <v-icon left v-if="!saving">mdi-content-save</v-icon>
            {{ isEditing ? 'Update Template' : 'Save Template' }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card class="rounded-xl overflow-hidden">
        <v-card-title class="bg-red-50 text-red-800 py-4 px-6 font-bold">
          Delete Template?
        </v-card-title>
        <v-card-text class="pa-6 text-gray-600">
          Are you sure you want to delete
          <span class="font-bold text-gray-800">"{{ selectedLetter?.name }}"</span>? This action
          cannot be undone and any notifications linked to this letter will fail to attach it.
        </v-card-text>
        <v-card-actions class="pa-4 bg-gray-50">
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false" class="normal-case">Cancel</v-btn>
          <v-btn color="error" depressed @click="deleteLetter" class="normal-case rounded-lg px-6">
            Confirm Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { VueEditor } from 'vue2-editor'
import { getNotificationTriggerOptions } from '@/utils/workforce/constants'

const DUMMY_LETTERS = [
  {
    _id: 'dummy1',
    name: 'Standard Offer Letter',
    content:
      '<h1>Offer Letter</h1><p>Dear {{customer_name}},</p><p>We are pleased to offer you...</p>',
    updatedAt: new Date().toISOString(),
    createdAt: new Date().toISOString()
  },
  {
    _id: 'dummy2',
    name: 'Loan Disbursement Notice',
    content:
      '<h1>Disbursement Notice</h1><p>Your loan of {{loan_amount}} has been disbursed to account {{account_number}}.</p>',
    updatedAt: new Date().toISOString(),
    createdAt: new Date().toISOString()
  }
]

export default {
  name: 'LetterTemplates',
  components: { VueEditor },
  data() {
    return {
      letters: [],
      loading: false,
      saving: false,
      search: '',
      dialog: false,
      deleteDialog: false,
      isEditing: false,
      selectedLetter: null,
      form: {
        name: '',
        content: ''
      },
      customToolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'],
        [{ header: 1 }, { header: 2 }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ direction: 'rtl' }],
        [{ size: ['small', false, 'large', 'huge'] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ color: [] }, { background: [] }],
        [{ font: [] }],
        [{ align: [] }],
        ['clean'],
        ['link', 'image', 'video']
      ]
    }
  },
  computed: {
    filteredLetters() {
      if (!this.search) return this.letters
      const s = this.search.toLowerCase()
      return this.letters.filter(
        (l) =>
          (l.name && l.name.toLowerCase().includes(s)) ||
          (l.content && l.content.toLowerCase().includes(s))
      )
    },
    parameterOptions() {
      return [
        { text: 'Customer Name', value: 'customer_name' },
        { text: 'Loan Amount', value: 'loan_amount' },
        { text: 'Disbursement Date', value: 'disbursement_date' },
        { text: 'EMI Amount', value: 'emi_amount' },
        { text: 'Outstanding Amount', value: 'outstanding_amount' },
        { text: 'Due Date', value: 'due_date' },
        { text: 'Account Number', value: 'account_number' },
        { text: 'Reference Number', value: 'reference_number' },
        { text: 'Late Fee Amount', value: 'late_fee_amount' },
        { text: 'Days Overdue', value: 'days_overdue' }
      ]
    }
  },
  async mounted() {
    await this.fetchLetters()
  },
  methods: {
    async fetchLetters() {
      try {
        this.loading = true
        // Try to fetch from API first
        try {
          const response = await this.$axios.get('/orgSetup/letter-templates')
          this.letters = response.data?.letters || []
        } catch (apiError) {
          console.warn('API fetch failed, falling back to localStorage')
        }

        // Merge with localStorage
        const stored = localStorage.getItem('finfi_letter_templates')
        let localLetters = stored ? JSON.parse(stored) : []

        // Show dummy templates if no templates exist anywhere
        if (this.letters.length === 0 && localLetters.length === 0) {
          localLetters = [...DUMMY_LETTERS]
        }

        // Combine them (prioritize API, then local)
        const combined = [...this.letters]
        localLetters.forEach((ll) => {
          if (!combined.find((cl) => cl._id === ll._id)) {
            combined.push(ll)
          }
        })

        this.letters = combined
      } catch (error) {
        console.error('Error fetching letters:', error)
      } finally {
        this.loading = false
      }
    },
    insertParameter(param) {
      if (param) {
        const variableText = `{{${param}}}`
        this.form.content += variableText
        this.$toast.info(`Inserted {{${param}}}`)
      }
    },
    openCreateDialog() {
      this.isEditing = false
      this.form = { name: '', content: '' }
      this.dialog = true
    },
    editLetter(letter) {
      this.isEditing = true
      this.selectedLetter = letter
      this.form = {
        name: letter.name,
        content: letter.content
      }
      this.dialog = true
    },
    async saveLetter() {
      if (!this.form.name) {
        this.$toast.error('Letter name is required')
        return
      }

      try {
        this.saving = true
        const payload = {
          name: this.form.name,
          content: this.form.content,
          type: 'LETTER',
          updatedAt: new Date().toISOString()
        }

        // Primary: Save to API
        try {
          if (this.isEditing) {
            await this.$axios.put(`/orgSetup/letter-templates/${this.selectedLetter._id}`, payload)
          } else {
            await this.$axios.post('/orgSetup/letter-templates', payload)
          }
        } catch (apiError) {
          console.warn('API save failed, saving to localStorage')
        }

        // Secondary: Save to LocalStorage
        const stored = localStorage.getItem('finfi_letter_templates')
        let localLetters = stored ? JSON.parse(stored) : []

        if (this.isEditing) {
          const idx = localLetters.findIndex((l) => l._id === this.selectedLetter._id)
          if (idx !== -1) {
            localLetters[idx] = { ...this.selectedLetter, ...payload }
          } else {
            // If it was from API but API failed to update, or dummy
            localLetters.push({ ...this.selectedLetter, ...payload })
          }
        } else {
          localLetters.push({
            _id: 'local_' + Date.now(),
            ...payload,
            createdAt: new Date().toISOString()
          })
        }

        localStorage.setItem('finfi_letter_templates', JSON.stringify(localLetters))
        this.$toast.success('Letter template saved successfully (LocalStorage)')

        this.dialog = false
        await this.fetchLetters()
      } catch (error) {
        console.error('Error saving letter:', error)
        this.$toast.error('Failed to save letter template')
      } finally {
        this.saving = false
      }
    },
    confirmDelete(letter) {
      this.selectedLetter = letter
      this.deleteDialog = true
    },
    async deleteLetter() {
      try {
        // Try Delete from API
        try {
          await this.$axios.delete(`/orgSetup/letter-templates/${this.selectedLetter._id}`)
        } catch (apiError) {
          // ignore
        }

        // Delete from LocalStorage
        const stored = localStorage.getItem('finfi_letter_templates')
        if (stored) {
          let localLetters = JSON.parse(stored)
          localLetters = localLetters.filter((l) => l._id !== this.selectedLetter._id)
          localStorage.setItem('finfi_letter_templates', JSON.stringify(localLetters))
        }

        this.$toast.success('Letter template deleted')
        this.deleteDialog = false
        await this.fetchLetters()
      } catch (error) {
        console.error('Error deleting letter:', error)
        this.$toast.error('Failed to delete letter template')
      }
    },
    formatDate(dateStr, dateOnly = false) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      if (dateOnly) return date.toLocaleDateString('en-IN')

      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.letter-templates-container {
  padding: 24px;
  background-color: #f9fafb;
  min-height: 80vh;
}

.letter-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background-color: white;
  border: 1px solid #d1d5db;
}

/* .letter-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 30px -10px rgba(0, 0, 0, 0.1) !important;
} */

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.form-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #21427d;
  margin: 0;
}

.top-header {
  position: sticky;
  top: 0;
  background-color: #fff;
  z-index: 50;
}

.shadow-top {
  box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.05);
}

.param-chip {
  transition: all 0.2s ease;
  border: 1px solid #d1d5db;
}

.param-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.editor-wrapper {
  background: white;
}

/* Customizing scrollbar for scrollable text area */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}
.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 10px;
}
</style>
